/**
 * Renk KARŞITLIĞI (WCAG 2.2, 1.4.3 "Contrast (Minimum)").
 *
 * Çekirdekte çünkü ÜÇ taraf aynı sayıyı okumak zorunda: widget'ın balonu ve sohbet penceresi,
 * açılır pencere yüzeyi ve PANEL. Panelde "bu renk okunur" diyen hesap ile ziyaretçinin
 * gördüğü rengi seçen hesap ayrışırsa, işletmeye yeşil gösterip müşteriye okunmaz bir yazı
 * göndermiş oluruz — ve bunu kimse fark etmez.
 *
 * Hesap saf matematik: ne I/O var ne tarayıcı. Aynı kod sunucuda, panelde ve ziyaretçinin
 * tarayıcısında koşuyor.
 */

/** WCAG AA: normal boy yazı için en az 4,5:1. */
export const AA_CONTRAST = 4.5;

/** WCAG AA: büyük yazı (≥ 18,66px kalın ya da ≥ 24px) için 3:1. */
export const AA_LARGE_CONTRAST = 3;

export const WHITE = '#ffffff';
/** Yumuşak koyu ton; saf siyahtan hoş durduğu için ilk tercih. */
export const SOFT_BLACK = '#111827';
/**
 * Son çare: saf siyah.
 *
 * Yumuşak koyu ton orta tonlu bir marka renginde 4,5:1'i geçmeyebiliyor. Saf siyah ile beyaz
 * arasında her renk için en az 4,58:1 var — yani hiçbir marka rengi AA eşiğinin altında
 * kalmıyor.
 */
export const BLACK = '#000000';

/** `#rrggbb` → kanal değerleri (0–255); biçim doğrulanmış varsayılıyor. */
const channels = (hex: string): readonly [number, number, number] => [
  Number.parseInt(hex.slice(1, 3), 16),
  Number.parseInt(hex.slice(3, 5), 16),
  Number.parseInt(hex.slice(5, 7), 16),
];

/** Kanalı WCAG'in doğrusal uzayına taşıyor (sRGB gama düzeltmesinin tersi). */
const linear = (value: number): number => {
  const channel = value / 255;
  return channel <= 0.040_45 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
};

/** `#rrggbb` → WCAG göreli parlaklık (0 siyah, 1 beyaz). */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** İki rengin karşıtlık oranı (1 ile 21 arası). */
export const contrastRatio = (a: string, b: string): number => {
  const first = luminance(a);
  const second = luminance(b);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
};

/**
 * Marka rengi ÜSTÜNDE okunur yazı rengi.
 *
 * Marka rengi her tonda olabilir; beyaz ile koyu arasından karşıtlığı yüksek olan seçiliyor.
 * Seçilen renk AA eşiğini geçmiyorsa saf siyaha düşülüyor: işletmenin seçtiği marka rengini
 * DEĞİŞTİRMEK yerine yazıyı koyultmak, hem markayı hem okunurluğu koruyor.
 */
export function readableInkOn(accent: string): typeof WHITE | typeof SOFT_BLACK | typeof BLACK {
  const onWhite = contrastRatio(accent, WHITE);
  const onSoft = contrastRatio(accent, SOFT_BLACK);
  if (onWhite >= onSoft) return onWhite >= AA_CONTRAST ? WHITE : BLACK;
  return onSoft >= AA_CONTRAST ? SOFT_BLACK : BLACK;
}

/** Kanalları siyaha doğru ölçeklerken kullanılan adım; 24 adım saf siyaha kadar iniyor. */
const DARKEN_STEP = 0.96;
const DARKEN_MAX_STEPS = 24;

const toHex = (value: number): string =>
  Math.max(0, Math.min(255, Math.round(value)))
    .toString(16)
    .padStart(2, '0');

/**
 * Marka renginin BEYAZ ÜSTÜNDE yazı olarak okunur hâli.
 *
 * Marka rengi çoğu zaman bir ARKA PLAN; ama bağlantılarda ve etiketlerde yazı rengi olarak da
 * kullanılıyor ve açık bir marka rengi (sarı, açık yeşil) beyaz üstünde okunmuyor. İşletmenin
 * rengini reddetmek yerine KOYULTULMUŞ hâli kullanılıyor: renk tanınır kalıyor, yazı okunuyor.
 *
 * **Kanallar siyaha doğru ÖLÇEKLENİYOR, HSL'e çevrilmiyor.** Üç kanalı aynı katsayıyla çarpmak
 * oranlarını korur, yani RENK TONU değişmez — yalnız koyulaşır. HSL dönüşümü aynı sonucu daha
 * çok kodla verirdi ve ziyaretçinin tarayıcısına inen her bayt ölçülüyor.
 *
 * Eşiğe ulaşılamazsa (teoride olmaz; siyah 21:1 veriyor) saf siyah dönüyor.
 */
export function accentOnWhite(accent: string): string {
  if (contrastRatio(accent, WHITE) >= AA_CONTRAST) return accent;

  let [r, g, b] = channels(accent);
  for (let step = 0; step < DARKEN_MAX_STEPS; step += 1) {
    r *= DARKEN_STEP;
    g *= DARKEN_STEP;
    b *= DARKEN_STEP;
    const darker = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    if (contrastRatio(darker, WHITE) >= AA_CONTRAST) return darker;
  }
  return BLACK;
}
